import React from 'react';
import { Badge, Button, Table } from 'react-bootstrap';
import type { ChangeSubtask } from '../../../types/changes';
import { assignmentLabel } from '../changeSubtaskAssignment';
import { subtaskListStatus, SUBTASK_STATUS_LABELS } from '../changeLabels';
import {
  fieldLabelByColumnId,
  internalSubtaskChecklistCellText,
  parseInternalSubtaskFieldColumnId,
  type InternalSubtaskTableColumnId,
  type InternalSubtaskTableColumnOption,
} from '../changeInternalSubtaskTableColumns';
import type { InternalSubtaskListLayoutColumnView } from '../changeInternalSubtaskListLayout';
function subtaskStatusBadge(subtask: Pick<ChangeSubtask, 'status' | 'invalidated_at'>) {
  const status = subtaskListStatus(subtask);
  if (status === 'cancelada') return <Badge bg="secondary">Cancelada</Badge>;
  const label = SUBTASK_STATUS_LABELS[status] || status;
  if (status === 'concluida') return <Badge bg="success">{label}</Badge>;
  if (status === 'em_atendimento') return <Badge bg="primary">{label}</Badge>;
  return <Badge bg="secondary">{label}</Badge>;
}

function subtaskMemberLabel(subtask: ChangeSubtask): string {
  return assignmentLabel(subtask.assignment_kind, subtask.assignee_name);
}

function subtaskTypeLabel(subtask: ChangeSubtask): string {
  return (subtask.task_type_name || '').trim() || '—';
}

function presetCellText(subtask: ChangeSubtask, specKey: string): string {
  const items = subtask.checklist?.items || [];
  const field = items.find((item) => (item.preset_spec_key || '').trim() === specKey);
  if (!field?.id) return '—';
  return internalSubtaskChecklistCellText(subtask, field.id);
}

function cellContent(
  columnId: InternalSubtaskTableColumnId | string,
  subtask: ChangeSubtask,
): React.ReactNode {
  if (typeof columnId === 'string' && columnId.startsWith('preset:')) {
    return presetCellText(subtask, columnId.slice('preset:'.length));
  }
  const fieldId = parseInternalSubtaskFieldColumnId(String(columnId));
  if (fieldId != null) {
    return internalSubtaskChecklistCellText(subtask, fieldId);
  }
  switch (columnId) {
    case 'title':
      return subtask.title;
    case 'group_name':
      return subtask.group_name || '—';
    case 'status':
      return subtaskStatusBadge(subtask);
    case 'member':
      return subtaskMemberLabel(subtask);
    case 'task_type':
      return subtaskTypeLabel(subtask);
    default:
      return '—';
  }
}

interface Props {
  subtasks: ChangeSubtask[];
  columns: Array<InternalSubtaskListLayoutColumnView | InternalSubtaskTableColumnId>;
  catalog: InternalSubtaskTableColumnOption[];
  onOpenSubtask: (subtaskId: number) => void;
}

const ChangeInternalSubtaskListTable: React.FC<Props> = ({
  subtasks,
  columns,
  catalog,
  onOpenSubtask,
}) => (
  <Table responsive hover className="mb-0 align-middle">
    <thead>
      <tr>
        {columns.map((column) => {
          const columnId = typeof column === 'string' ? column : column.tableColumnId;
          const label =
            typeof column === 'string' ? fieldLabelByColumnId(column, catalog) : column.label;
          const key = typeof column === 'string' ? column : column.columnKey;
          return (
            <th key={key} scope="col">
              {label}
            </th>
          );
        })}
        <th scope="col" className="text-end">Ação</th>
      </tr>
    </thead>
    <tbody>
      {subtasks.map((subtask) => (
        <tr key={subtask.id}>
          {columns.map((column) => {
            const columnId = typeof column === 'string' ? column : column.tableColumnId;
            const key = typeof column === 'string' ? column : column.columnKey;
            return <td key={`${subtask.id}-${key}`}>{cellContent(columnId, subtask)}</td>;
          })}
          <td className="text-end">
            <Button
              size="sm"
              variant="outline-primary"
              onClick={() => onOpenSubtask(subtask.id)}
              aria-label={`Ver ${subtask.title}`}
            >
              Ver
            </Button>
          </td>
        </tr>
      ))}
    </tbody>
  </Table>
);

export default ChangeInternalSubtaskListTable;
